import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Plus, Send, Archive, Search, Link2, MailOpen } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const when = (s) => s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

export default function Messages() {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const [threads, setThreads] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [dlg, setDlg] = useState(false);
  const [nf, setNf] = useState({ teacher_id: "", subject: "", text: "" });

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (isDirector && showArchived) params.archived = true;
    api.get("/messages", { params }).then((r) => setThreads(r.data)).catch(() => {});
  };
  useEffect(load, [q, showArchived]);
  useEffect(() => { if (isDirector) api.get("/people/teachers").then((r) => setTeachers(r.data)).catch(() => {}); }, [isDirector]);

  const open = async (t) => { try { const r = await api.get(`/messages/${t.id}`); setActive(r.data); load(); } catch { toast.error("Error"); } };
  const send = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/messages/${active.id}/reply`, { text: reply }); setReply(""); const r = await api.get(`/messages/${active.id}`); setActive(r.data); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const create = async () => {
    if (!nf.subject.trim() || !nf.text.trim() || (isDirector && !nf.teacher_id)) { toast.error("Fill in all fields"); return; }
    try { const r = await api.post("/messages", nf); toast.success("Message sent"); setDlg(false); setNf({ teacher_id: "", subject: "", text: "" }); load(); setActive(r.data && r.data.id ? (await api.get(`/messages/${r.data.id}`)).data : null); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const archive = async () => { try { await api.post(`/messages/${active.id}/archive`); toast.success("Toggled archive"); setActive(null); load(); } catch { toast.error("Error"); } };

  return (
    <div className="stagger-in">
      <PageHeader title="Staff Messages" subtitle={isDirector ? "Private conversations with each teacher." : "Private messages with the director."} testid="messages-page"
        action={<Button data-testid="new-message-button" onClick={() => setDlg(true)} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />New Message</Button>} />

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative"><Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" /><Input data-testid="messages-search" className="rounded-full h-9 pl-9 bg-white w-56" placeholder="Search conversations…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        {isDirector && <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="messages-archived-toggle" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />Show archived</label>}
      </div>

      <div className="grid md:grid-cols-[320px_1fr] gap-4">
        <div className="space-y-2">
          {threads.length === 0 ? <EmptyState icon={MessageSquare} title="No conversations" hint="Start one with New Message." testid="messages-empty" /> :
            threads.map((t) => (
              <button key={t.id} data-testid={`thread-${t.id}`} onClick={() => open(t)}
                className={`w-full text-left bg-white rounded-2xl border p-3 transition-colors ${active?.id === t.id ? "border-teal" : "border-gray-100 hover:border-teal-light"}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold truncate">{t.subject}</p>
                  {t.unread > 0 && <span className="shrink-0 bg-teal text-white text-[10px] rounded-full px-1.5 py-0.5" data-testid={`thread-unread-${t.id}`}>{t.unread}</span>}
                </div>
                {isDirector && <p className="text-xs text-muted-foreground">{t.teacher_name}</p>}
                <p className="text-xs text-muted-foreground truncate mt-0.5">{t.preview}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{when(t.last_at)}{t.archived ? " · archived" : ""}</p>
              </button>
            ))}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 min-h-[300px]">
          {!active ? <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div> :
            <div className="flex flex-col h-full">
              <div className="flex items-start justify-between gap-3 border-b pb-3 mb-3">
                <div>
                  <h3 className="font-head font-bold">{active.subject}</h3>
                  <p className="text-xs text-muted-foreground">{isDirector ? active.teacher_name : "with the Director"}{active.ref_label ? ` · re: ${active.ref_label}` : ""}</p>
                </div>
                {isDirector && <Button size="sm" variant="outline" className="rounded-full" data-testid="archive-thread" onClick={archive}><Archive className="w-3.5 h-3.5 mr-1" />{active.archived ? "Unarchive" : "Archive"}</Button>}
              </div>
              <div className="space-y-3 flex-1 overflow-y-auto max-h-[45vh]">
                {(active.posts || []).map((p) => {
                  const mine = p.by_user_id === user?.user_id;
                  return (
                    <div key={p.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`post-${p.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${mine ? "bg-teal text-white" : "bg-background"}`}>
                        <p className="text-[10px] opacity-70 mb-0.5">{p.by_name} · {when(p.at)}</p>
                        <p className="text-sm whitespace-pre-wrap">{p.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-3 border-t mt-3">
                <Textarea data-testid="reply-input" rows={1} className="resize-none" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" />
                <Button data-testid="reply-send" className="rounded-full bg-teal hover:bg-teal-dark self-end" onClick={send}><Send className="w-4 h-4" /></Button>
              </div>
            </div>}
        </div>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><MessageSquare className="w-5 h-5 text-teal" />New Message</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {isDirector && <div className="space-y-1.5"><Label>To teacher</Label>
              <select data-testid="new-message-teacher" className="w-full border rounded-md h-10 px-3" value={nf.teacher_id} onChange={(e) => setNf({ ...nf, teacher_id: e.target.value })}>
                <option value="">Select…</option>{teachers.map((t) => <option key={t.id} value={t.id}>{t.preferred_name || `${t.first_name} ${t.last_name}`}</option>)}
              </select></div>}
            {!isDirector && <p className="text-sm text-muted-foreground">This message goes privately to the Director.</p>}
            <div className="space-y-1.5"><Label>Subject</Label><Input data-testid="new-message-subject" value={nf.subject} onChange={(e) => setNf({ ...nf, subject: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Message</Label><Textarea data-testid="new-message-text" rows={4} value={nf.text} onChange={(e) => setNf({ ...nf, text: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="send-new-message" onClick={create} className="rounded-full bg-teal hover:bg-teal-dark">Send</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
